import React, { useState } from 'react';

/**
 * PoweredByRevstar - A floating badge component displayed on merchant storefronts
 * Shows "Powered by Revstar" for merchants without the badge removal feature
 * Merchants with access to 'branding.remove_badge' feature won't see this badge
 *
 * Tracking parameters:
 * - utm_source: revbadge (identifies traffic from the badge)
 * - utm_medium: storefront (identifies it's from a merchant storefront)
 * - utm_campaign: powered_by (campaign name)
 * - ref: store slug (identifies which store the click came from)
 */
const PoweredByRevstar = ({ canRemoveBadge, storeSlug, position = 'bottom-right' }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Don't render if merchant has access to remove badge feature
  if (canRemoveBadge) {
    return null;
  }

  // Position styles based on prop
  const positionStyles = {
    'bottom-right': { bottom: '20px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' },
  };

  const currentPosition = positionStyles[position] || positionStyles['bottom-right'];

  // Build tracking URL with UTM parameters and store reference
  const trackingUrl = `https://userevstar.com/pricing/?utm_source=revbadge&utm_medium=storefront&utm_campaign=powered_by${storeSlug ? `&ref=${encodeURIComponent(storeSlug)}` : ''}`

  return (
    <>
      <a
        href={trackingUrl}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="sf-powered-by-revstar"
        style={{
          position: 'fixed',
          ...currentPosition,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          backgroundColor: isHovered ? '#1a1a1a' : '#000000',
          color: '#ffffff',
          borderRadius: '8px',
          textDecoration: 'none',
          fontSize: '13px',
          fontWeight: '500',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxShadow: isHovered
            ? '0 8px 25px rgba(0, 0, 0, 0.3)'
            : '0 4px 15px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          cursor: 'pointer',
        }}
      >
        {/* Revstar Logo/Icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          style={{ flexShrink: 0 }}
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            fill="#0D7463"
            stroke="#0D7463"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span style={{ letterSpacing: '0.3px' }}>
          Powered by <span style={{ fontWeight: '700' }}>Revstar</span>
        </span>
      </a>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 480px) {
          a[href*="userevstar.com"] {
            padding: 8px 12px !important;
            font-size: 12px !important;
            bottom: 12px !important;
            right: 12px !important;
          }
          a[href*="userevstar.com"] svg {
            width: 16px !important;
            height: 16px !important;
          }
        }
      `}</style>
    </>
  );
};

export default PoweredByRevstar;
